import { useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { PageHeader, Button, Card, Table, Map } from "@cofano/cck";
import { Tabs, Tab, Box, Grid2 as Grid } from "@mui/material";
import { Layers } from "@mui/icons-material";

import useTabs from "components/hooks/useTabs";
import Helmet from "components/helmet/Helmet";
import { OpeningHoursDetails } from "components/openinghours/OpeningHours";
import Documents from "components/documents/Documents";
import PageHeaderButtons from "components/buttons/PageHeaderButtons";

import { Language, usePermission } from "imports/Utils";
import { RelationLink } from "utils/LinkUtils";

import { getMapCenterPosition } from "view/maps/utils/MapUtils";

import LocationGeneralDetails from "./components/details/LocationGeneralDetails";
import LocationAddressDetails from "./components/details/LocationAddressDetails";
import LocationQuaysDetails from "./components/details/LocationQuaysDetails";
import LocationContactsTable from "./components/table/LocationContactsTable";

import { cleanlyDecodePolyline, hasEditLocationPermission, LocationTabs, MAP_HEIGHT } from "./utils/LocationUtils";
import { GeofenceMapApi, getGeofenceLabels, MapRef, MapsRef, useGeofenceShapes, useOtherGeofences } from "./utils/GeofenceUtils";
import MapOverlayControls from "./components/map/MapOverlayControls";

import { ChildProps } from "types/commons";

import { GetLocationDocument, GetLocationQuery } from "__generated__/view/locations/graphql/location.queries.generated";
import { LocationUserType, DocumentEntity } from "__generated__/enums";

export type LocationFromQuery = ChildProps<GetLocationQuery, "location">;

const LocationDetails = () => {

	const session = useStoreState(store => store.session);
	const navigate = useNavigate();
	const { id } = useParams();

	const useLocationProps = useLocation();

	const { isRTR } = usePermission();

	const canEdit = hasEditLocationPermission(session);

	const { selectedTab, selectTab } = useTabs({
		defaultSelected: useLocationProps.state?.tab || LocationTabs.DETAILS,
	});

	const { data, loading } = useQuery(GetLocationDocument, {
		variables: {
			id: Number(id),
		},
	});

	const [ mapApi, setMapApi ] = useState<GeofenceMapApi>();
	const [ showOtherGeofences, setShowOtherGeofences ] = useState(false);

	const otherGeofences = useOtherGeofences(Number(id));

	const shownGeofences = useMemo(
		() => showOtherGeofences ? otherGeofences : [],
		[ otherGeofences, showOtherGeofences ],
	);

	useGeofenceShapes(mapApi, shownGeofences, true, geofence => {
		navigate(`/view/locations/${geofence.id}/details`, { state: { tab: LocationTabs.MAP } });
	});

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{data?.location?.description || Language("LOCATION")}
					<Helmet title={data?.location?.description || intl.formatMessage({ id: "LOCATION" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<PageHeaderButtons
						items={[ {
							label: intl.formatMessage({ id: "LOCATIONS" }),
							visible: isRTR,
							onClick: () => navigate("/view/locations"),
							variant: "outlined",
						}, {
							label: intl.formatMessage({ id: "MOORING_AVAILABILITIES" }),
							visible: isRTR,
							onClick: () => navigate(`/view/quayavailabilities?location=${data?.location?.id}`),
							variant: "outlined",
							loading: loading,
						}, {
							label: intl.formatMessage({ id: "EDIT" }),
							visible: canEdit,
							onClick: () => navigate(`/view/locations/${data?.location?.id}/edit${window.location.search}`, { state: { tab: selectedTab } }),
							variant: "contained",
						} ]}
					/>
				</PageHeader.Actions>
			</PageHeader>

			<Box mb={2}>
				<Card disablePadding>
					<Tabs
						value={selectedTab}
						onChange={(e, value) => selectTab(value)}
						indicatorColor="primary"
						variant="scrollable">
						<Tab value={LocationTabs.DETAILS} label={Language("DETAILS")} />
						<Tab value={LocationTabs.CONTACTS} label={intl.formatMessage({ id: "CONTACTS" })} />
						<Tab value={LocationTabs.MAP} label={Language("LOCATION_MAP")} />
						<Tab value={LocationTabs.OPENING_HOURS} label={Language("LOCATION_OPENING_HOURS")} />
						<Tab value={LocationTabs.RELATIONS} label={Language("RELATIONS")} />
						<Tab value={LocationTabs.QUAYS} label={Language("QUAYS")} />
						<Tab value={LocationTabs.DOCUMENTS} label={intl.formatMessage({ id: "DOCUMENTS" })} />
					</Tabs>
				</Card>
			</Box>

			{selectedTab === LocationTabs.DETAILS && (
				<Grid spacing={3} container>
					<Grid size={{ lg: 4, md: 4, xs: 12 }}>
						<Card title={intl.formatMessage({ id: "DETAILS" })}>
							<LocationGeneralDetails location={data?.location ?? null}  />
						</Card>
					</Grid>

					<Grid size={{ lg: 4, md: 4, xs: 12 }}>
						<Card title={intl.formatMessage({ id: "ADDRESS" })}>
							<LocationAddressDetails address={data?.location?.address} />
						</Card>
					</Grid>

				</Grid>
			)}

			{selectedTab === LocationTabs.CONTACTS && (
				<Card disablePadding>
					<LocationContactsTable contacts={data?.location?.contacts ?? []} />
				</Card>
			)}

			{selectedTab === LocationTabs.MAP && (
				<Grid container spacing={3}>
					<Grid size={12}>
						<Card disablePadding>
							<Box sx={{ height: MAP_HEIGHT, position: "relative", width: "100%" }}>
								<MapOverlayControls>
									<Button
										icon={<Layers />}
										variant={showOtherGeofences ? "contained" : "text"}
										onClick={() => setShowOtherGeofences(!showOtherGeofences)} />
								</MapOverlayControls>

								<Map
									apiKey={"AIzaSyAFEhlhSJ7c85rDGz1JX99veD0sshXx_xs"}
									center={getMapCenterPosition(session, {
										latitude: data?.location?.position?.latitude,
										longitude: data?.location?.position?.longitude,
									})}
									polyLines={cleanlyDecodePolyline(data?.location?.encodedPolyline || "")}
									onGoogleApiLoaded={(map: MapRef, maps: MapsRef) => setMapApi({ map: map, maps: maps })}
									markers={[
										...(data?.location?.position?.latitude && data?.location.position.longitude ? [ {
											lat: data.location.position.latitude,
											lng: data.location.position.longitude,
										} ] : []),
										...getGeofenceLabels(shownGeofences),
									]}
									disableBounds
									zoom={15} />
							</Box>
						</Card>
					</Grid>
				</Grid>
			)}

			{selectedTab === LocationTabs.OPENING_HOURS && (
				<Grid spacing={3} container>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>
						<Card>
							<OpeningHoursDetails hours={data?.location?.openingHours ?? null} />
						</Card>
					</Grid>
				</Grid>
			)}

			{selectedTab === LocationTabs.RELATIONS && (
				<Grid spacing={3} container>
					<Grid size={{ lg: 6, md: 6, xs: 12 }}>
						<Card disablePadding>
							<Table
								id="locationRelationUsersTable"
								content={(data?.location?.relationUsers ?? []).map((it, index) => ({ ...it, id: index }))}
								columns={[ {
									id: "customer",
									title: intl.formatMessage({ id: "CUSTOMER" }),
									render: row => <RelationLink relation={row.customer} />,
								}, {
									id: "type",
									title: intl.formatMessage({ id: "TYPE" }),
									render: (row) => intl.formatMessage({ id: row.type === LocationUserType.Owner ? "LOCATION_OWNER" : "LOCATION_USER" }),
								} ]}
								disableFooter />
						</Card>
					</Grid>
				</Grid>
			)}

			{selectedTab === LocationTabs.QUAYS && (
				<LocationQuaysDetails quays={data?.location?.quays ?? []} />
			)}

			{selectedTab === LocationTabs.DOCUMENTS && data?.location && (
				<Card disablePadding>
					<Documents
						entities={[ {
							id: data.location.id,
							name: data.location.description,
							type: DocumentEntity.Location,
						} ]} />
				</Card>
			)}
		</div>
	);
};

export default LocationDetails;
